import { useLocation, useNavigate, useParams } from 'react-router';

import { BackLink, DetailNotFoundPage } from '@/components/admin/detail-parts';
import { RouteError, RouteLoading } from '@/components/ui/route-state';
import { useTranslations } from '@/i18n/use-locale';
import { listReturnPath } from '@/lib/detail';
import type { ModerationSubject } from '@/lib/query-keys';
import { useModerationItemQuery, useModerationQueueQuery } from '@/lib/queries';
import { OrganizationReview } from './organization-review';
import { DocumentReview } from './document-review';

const LIST_PATH: Record<ModerationSubject, string> = {
  organization: '/verification',
  document: '/documents',
};

/**
 * One item of a review queue, in full: a club's registration with its CEO letter
 * embedded, or a talent's credential document with the file beside it — and the
 * decision, on the same screen as the evidence.
 *
 * Mounted twice, at `/verification/:itemId` and `/documents/:itemId`, so each kind of
 * item sits behind the permission of the queue it comes from (`nav.ts` resolves a
 * detail path to its list's grant).
 *
 * `GET /admin/moderation/queue/{itemId}` is new on the API. Until it ships it answers
 * an error, and the item is looked up in the queue list instead — the same query the
 * list screen ran, so arriving from the list costs no request at all and a refresh
 * costs one. A decided item is no longer in that list; then the screen says it cannot
 * find it rather than guessing.
 */
export function ModerationDetailRoute({ subject }: { subject: ModerationSubject }) {
  const itemId = useParams().itemId ?? '';
  const t = useTranslations('admin.review');
  const location = useLocation();
  const navigate = useNavigate();
  const listPath = LIST_PATH[subject];
  const backTo = listReturnPath(location.state, listPath);
  const backLabel = subject === 'organization' ? t('backToVerification') : t('backToDocuments');

  const itemQuery = useModerationItemQuery(subject, itemId);

  // The by-id read is new on the API; while it answers 404, and whenever it fails,
  // the cached queue list stands in for it.
  const needsFallback = itemQuery.isError;

  const queueQuery = useModerationQueueQuery(subject, { enabled: needsFallback });

  if (itemQuery.isPending || (needsFallback && queueQuery.isPending)) return <RouteLoading />;

  const fromEndpoint = itemQuery.data ?? null;
  const item =
    fromEndpoint ?? queueQuery.data?.items.find((candidate) => candidate.id === itemId) ?? null;

  if (!item) {
    if (needsFallback && queueQuery.isError) {
      return (
        <RouteError
          error={queueQuery.error}
          onRetry={() => {
            void itemQuery.refetch();
            void queueQuery.refetch();
          }}
        />
      );
    }
  }

  // An item of the other queue under this path is as absent as a missing one: it is
  // behind another permission, and the decision call would name the wrong subject.
  if (!item || item.subject !== subject) {
    return (
      <DetailNotFoundPage
        title={t('notFound.title')}
        hint={t('notFound.hint')}
        backTo={backTo}
        backLabel={backLabel}
      />
    );
  }

  const onDecided = () => void navigate(backTo);

  return (
    <div className="space-y-6">
      <BackLink to={backTo} label={backLabel} />
      {subject === 'organization' ? (
        <OrganizationReview item={item} onDecided={onDecided} />
      ) : (
        <DocumentReview item={item} onDecided={onDecided} />
      )}
    </div>
  );
}
